import React, { useState, useEffect } from 'react';
import { generateCompleteDummyData, generateDummyEmployees, companies } from '../utils/dummyData';

/**
 * Demo component to showcase the enhanced dummy data generation
 * This component demonstrates the company-based employee ID generation
 * and comprehensive dummy data features
 */
const DummyDataDemo = () => {
  const [dummyData, setDummyData] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    // Generate dummy data on component mount
    const data = generateCompleteDummyData();
    setDummyData(data);
  }, []);

  if (!dummyData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Filter employees by selected company
  const filteredEmployees = selectedCompany === 'all'
    ? dummyData.employees
    : dummyData.employees.filter(emp => {
        const company = companies.find(c => c._id === emp.companyId);
        return company?.code === selectedCompany;
      });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Enhanced Dummy Data Demo
          </h1>
          <p className="text-gray-600 mb-4">
            This demo showcases the comprehensive dummy data generation system with company-based employee IDs,
            realistic employee data, and multi-company support.
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{dummyData.employees.length}</div>
              <div className="text-sm text-blue-800">Total Employees</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{dummyData.companies.length}</div>
              <div className="text-sm text-green-800">Companies</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{dummyData.departments.length}</div>
              <div className="text-sm text-purple-800">Departments</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{dummyData.designations.length}</div>
              <div className="text-sm text-orange-800">Designations</div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Filter by Company:</label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Companies</option>
                {companies.map(company => (
                  <option key={company.code} value={company.code}>
                    {company.name} ({company.code})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setShowStats(!showStats)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {showStats ? 'Hide Stats' : 'Show Stats'}
            </button>
          </div>
        </div>

        {/* Detailed Stats */}
        {showStats && (
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Detailed Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Company Distribution</h3>
                <div className="space-y-2">
                  {Object.entries(dummyData.stats.companyStats).map(([company, count]) => (
                    <div key={company} className="flex justify-between">
                      <span className="text-gray-600">{company}</span>
                      <span className="font-medium">{count} employees</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Employee Status</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active</span>
                    <span className="font-medium text-green-600">{dummyData.stats.activeEmployees}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Inactive</span>
                    <span className="font-medium text-red-600">{dummyData.stats.inactiveEmployees}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">On Leave</span>
                    <span className="font-medium text-yellow-600">{dummyData.stats.onLeaveEmployees}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Average Salary</span>
                    <span className="font-medium">${dummyData.stats.averageSalary.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Employee List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Employee List ({filteredEmployees.length} employees)
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Designation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Salary
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.slice(0, 20).map((employee) => {
                  const company = companies.find(c => c._id === employee.companyId);
                  return (
                    <tr key={employee._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {employee.employeeId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.firstName} {employee.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.department}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.designation}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company?.name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${employee.salary?.toLocaleString() || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          employee.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : employee.status === 'inactive'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {employee.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredEmployees.length > 20 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Showing first 20 employees. Total: {filteredEmployees.length}
              </p>
            </div>
          )}
        </div>

        {/* Usage Instructions */}
        <div className="bg-white shadow rounded-lg p-6 mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">How to Use</h2>
          <div className="prose prose-sm text-gray-600">
            <p>This enhanced dummy data system provides:</p>
            <ul>
              <li><strong>Company-based Employee IDs:</strong> Each employee gets an ID prefixed with their company code (e.g., TNS0001, GTI0002)</li>
              <li><strong>Realistic Data:</strong> Comprehensive employee profiles with skills, qualifications, emergency contacts, and more</li>
              <li><strong>Multi-Company Support:</strong> Distribute employees across multiple companies with proper associations</li>
              <li><strong>Complete HR Dataset:</strong> Includes departments, designations, leave types, and attendance records</li>
            </ul>
            <p className="mt-4">
              <strong>Import in your components:</strong>
            </p>
            <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
{`import { generateCompleteDummyData, generateDummyEmployees } from '../utils/dummyData';

// Generate complete dataset
const dummyData = generateCompleteDummyData();

// Or generate just employees
const employees = generateDummyEmployees(50);`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DummyDataDemo;
